import { buildHref } from "@softmax/web-addresses";
import { curatedThumb } from "@softmax/web-features/replay/art";
import { curatedLeagueName } from "@softmax/web-features/coworlds";
import { type StorePageContent } from "@/features/store-editor/records";
import { site, publicLinks } from "@softmax/web-addresses/discovery";
import { previewHeroArt } from "@/features/preview/content/preview-content";
import { type PreviewContent } from "../content/preview-content.types";
import { isLive } from "../data/preview-activity";
import { standingsFrom } from "../data/preview-standings";
import { storeRewards, trailFor } from "../data/preview-story";
import { settingsFacts } from "../metadata/league-settings";
import { detailsFrom } from "../metadata/preview-details";
import { requirementsFrom } from "../rules/preview-requirements";
import { previewStory, previewLadder } from "../preview-story.data";
import type { ResolvedPreviewLeague } from "../preview-league.data";
import type { PreviewResources } from "./read-preview-resources";
export function composePreviewContent({
  resolved,
  resources,
  includeDetails,
}: {
  resolved: ResolvedPreviewLeague;
  resources: PreviewResources;
  includeDetails: boolean;
}): PreviewContent {
  const {
    league,
    route,
    leagueSegment,
    coworldSlug,
    leagueCount,
    leagues,
    curatedName,
    coworldName,
    name,
  } = resolved;
  const { state, board, coworldRead, guide, storePage, media, rulesPage } =
    resources;
  const rules = settingsFacts(league.settings);
  const path = buildHref({
    name: "game.playMarkdown",
    game: route.game,
    league: leagueSegment,
  });
  const standings = state ? standingsFrom(state.standings) : [];
  const leader = standings[0] ?? null;
  const rawScoreLabel = state?.standings[0]?.score_label ?? null;
  const scoreLabel =
    rawScoreLabel?.trim().toLowerCase() === "typical episode"
      ? "Score"
      : rawScoreLabel;
  const rewards = storeRewards({
    settings: league.settings,
    divisionId: state?.divisionId ?? null,
    now: new Date(),
  });
  const storePageContent: StorePageContent | null = storePage?.content ?? null;

  const source = coworldRead?.source ?? null;
  const requirements = requirementsFrom(guide);
  const { blurb, closingLine, copy, about, team } = previewStory({
    preview: resolved,
    state,
    manifest: coworldRead?.manifest ?? null,
    source,
    scoreLabel,
    rules,
    rewards,
    content: storePageContent,
  });
  const commandHref = `${site.canonicalOrigin}${path}`;
  const details = detailsFrom({
    manifest: coworldRead?.manifest ?? null,
    documentationUrl: coworldRead?.documentationUrl ?? null,
    leagueCreatedAt: league.created_at,
    gameCreatedAt: league.game.created_at,
    commissionerKey: league.commissioner_key,
    seats: rules.seats,
    playerTypes: source?.playerTypes ?? null,
    source: team,
    guideHref: path,
    discordHref: publicLinks.discord,
  });
  const trail =
    board && leader
      ? trailFor({
          board,
          playerId: leader.playerId,
        })
      : null;
  const ladder = previewLadder({
    state,
    leader,
    rules,
  });
  return {
    viewer: resolved.viewer,
    generatedAt: new Date().toISOString(),
    detailsState: includeDetails ? "ready" : "pending",
    leagueId: league.id,
    slug: [coworldSlug, leagueSegment].filter(Boolean).join("/"),
    route,
    name,
    coworldName,
    heroUppercase: leagueSegment
      ? false
      : (curatedName?.heroUppercase ?? false),
    blurb,
    closingLine,
    command: `${site.canonicalOrigin.replace("https://", "")}${path}`,
    commandHref,
    art: curatedThumb(coworldSlug),
    heroArt: previewHeroArt[coworldSlug] ?? null,
    media,
    standings,
    playerCount: state?.playerCount ?? null,
    leagueName: curatedLeagueName(league),
    divisionName: state?.divisionName ?? null,
    leagueCount,
    leagues,
    scoreLabel,
    live: isLive({
      standings,
      newestCompletedAt: state?.newestCompletedAt ?? null,
      roundMinutes: rules.roundMinutes,
    }),
    rewards,
    lastRoundAt: state?.newestCompletedAt ?? null,
    rules,
    trail,
    ladder,
    about,
    rulesPage,
    requirements,
    doors: {
      team,
    },
    details,
    copy,
    creatorLinks: storePageContent?.links ?? [],
    icon: storePageContent?.icon ?? null,
  };
}
